import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * `showClose={false}` suppresses the corner ✕.
 *
 * For dialogs that carry their own explicit exit — the onboarding tour ends on
 * *Skip* / *Done*, and a second, unlabelled way out invites the click that dismisses the
 * thing without recording which of the two it was. Escape and the overlay still close it,
 * so nothing is trapped.
 *
 * ---
 *
 * **A dialog is never taller than the screen, and its fields are always reachable.**
 *
 * The panel is `fixed` and centred on `top-1/2 -translate-y-1/2`, which is right until the
 * form inside is taller than the viewport. Then it grows in *both* directions at once: the
 * last fields go below the fold and the header goes above it, and because the panel is
 * fixed there is nothing to scroll — the page behind does not move it and the panel has no
 * scrollbar of its own. On a laptop that was the property form, the tenancy form and the
 * party form; the operator could see neither the top of the form nor the Save button.
 *
 * The fix is two halves and both are needed:
 *
 *   - the panel is capped at `100dvh - 2rem`, so it always fits with a margin. `dvh`, not
 *     `vh`, because mobile browsers shrink the visual viewport when the URL bar is showing
 *     and `vh` keeps measuring the *large* viewport — the exact case where the Save button
 *     hides behind the browser chrome.
 *   - the children move into a scroll region with `min-h-0`. In a flex column a child's
 *     `min-height: auto` refuses to shrink below its content, so `overflow-y-auto` on its
 *     own would do nothing; `min-h-0` is what lets it become shorter than the form and
 *     scroll instead of pushing the panel open.
 *
 * The ✕ stays outside that region, so it is pinned to the corner while the fields move
 * under it rather than scrolling away with them.
 *
 * **`bodyClassName` styles the scroll region.** Padding and the gap between children live
 * there now, so a dialog that wants to be full-bleed — the command palette, the welcome
 * tour — passes `p-0` here rather than to `className`, which now reaches only the panel.
 */
export const DialogContent = React.forwardRef(
  ({ className, bodyClassName, children, showClose = true, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      {/* Radix traps focus and restores it on close — never hand-roll this (docs/04 §6). */}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed start-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-full max-w-lg -translate-y-1/2 flex-col overflow-hidden border bg-popover shadow-elevated sm:rounded-xl',
          'rtl:translate-x-1/2 ltr:-translate-x-1/2',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 duration-200',
          className,
        )}
        {...props}
      >
        {/* `overscroll-contain` keeps a flick at the end of the list from scrolling the
            page behind the overlay, which reads as the dialog jumping. */}
        <div className={cn('grid min-h-0 gap-4 overflow-y-auto overscroll-contain p-6', bodyClassName)}>
          {children}
        </div>
        {showClose ? (
          <DialogPrimitive.Close className="absolute end-4 top-4 rounded-md bg-popover p-1 opacity-70 ring-offset-background transition-opacity hover:bg-muted hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-1.5 text-start', className)} {...props} />
);

export const DialogFooter = ({ className, ...props }) => (
  <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />
);

export const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
